import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API}/marketplace/checkout/status/${sessionId}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });

      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
    }
  }, [sessionId, getAuthHeaders]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="checkout-success-page">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 pt-24 pb-12">
        <Card className="card-default text-center">
          <CardHeader className="pb-4">
            {status === 'loading' && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
                <CardTitle className="font-serif text-2xl">Processing Payment</CardTitle>
                <CardDescription>Please wait while we confirm your payment...</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <CardTitle className="font-serif text-2xl">Payment Successful!</CardTitle>
                <CardDescription>Thank you for your purchase</CardDescription>
              </>
            )}
            {status === 'failed' && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
                <CardTitle className="font-serif text-2xl">Payment Failed</CardTitle>
                <CardDescription>Something went wrong with your payment</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {status === 'success' && paymentData && (
              <div className="bg-muted/50 rounded-lg p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-medium">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span className="font-medium text-green-500">Paid</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {status === 'success' && (
                <>
                  <Button
                    className="w-full btn-primary"
                    onClick={() => navigate('/dashboard')}
                    data-testid="go-dashboard-btn"
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    You can now schedule meetings with your service providers from your dashboard.
                  </p>
                </>
              )}
              {status === 'failed' && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/marketplace')}
                  data-testid="back-marketplace-btn"
                >
                  Back to Marketplace
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
